import { FC, useCallback, useEffect, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  useThemeStore,
  useUserStore,
} from "../../../stores";
import { Column, Row } from "../../../components/containers";
import { SelectorInput, TextInput } from "../../../components/inputs";
import { Bank, ChequeBuilder, Customer, Timestamp } from "../../../types";
import { Heading } from "../../../components/typography";
import {
  AlignItems,
  JustifyContent,
} from "../../../components/containers/types";
import { ChequeCounter } from "../components";
import { FactorApi } from "../../../api";
import { addComma } from "../../../utils";
import { ChequeForm } from "../../../components/cheque";

const INITIAL_CHEQUE: ChequeBuilder = {
  name: "",
  account_no: "",
  bank: null,
  number: "",
  price: 0,
  shenase: "",
  date: Date.now(),
};

const INITIAL_FILE: (FileList | null)[] = [null];

const RecieveFactor: FC = () => {
  const theme = useThemeStore((s) => s.theme);
  const customers = useGeneralStore((s) => s.customers);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const [nrCheques, setNrCheques] = useState<number>(1);
  const [selectedChequeIdx, setSelectedChequeIdx] = useState<number>(0);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [desc, setDesc] = useState<string>("");
  const [totalPrice, setTotalPrice] = useState<string>("");

  const [cheques, setCheques] = useState<Array<ChequeBuilder>>([
    { ...INITIAL_CHEQUE },
  ]);
  const [files, setFiles] = useState<(FileList | null)[]>([...INITIAL_FILE]);

  const setChequesValue = useCallback(
    <T extends keyof ChequeBuilder>(field: T, value: ChequeBuilder[T]) => {
      setCheques((prev) => {
        prev[selectedChequeIdx][field] = value;
        return [...prev];
      });
    },
    [selectedChequeIdx]
  );

  const setFilesValue = useCallback(
    (v: FileList | null) => {
      setFiles((prev) => {
        prev[selectedChequeIdx] = v;
        return [...prev];
      });
    },
    [selectedChequeIdx]
  );

  useEffect(() => {
    let tp = 0;
    cheques.forEach((c) => {
      tp += c.price;
    });
    if (tp) setTotalPrice(addComma(tp));
    else setTotalPrice("");
  }, [cheques]);

  const onNext = useCallback(() => {
    if (selectedChequeIdx === nrCheques - 1) {
      setNrCheques((prev) => prev + 1);
      setCheques((prev) => [...prev, { ...INITIAL_CHEQUE }]);
      setFiles((prev) => [...prev, ...INITIAL_FILE]);
    } else {
      setSelectedChequeIdx((prev) => prev + 1);
    }
  }, [nrCheques, selectedChequeIdx]);

  useEffect(() => {
    setSelectedChequeIdx(nrCheques - 1);
  }, [nrCheques]);

  const validateCheques = useCallback((): boolean => {
    if (cheques.length !== files.length) return false;

    for (let i = 0; i < cheques.length; i++) {
      const c = cheques[i];
      if (!files[i]) return false;
      if (
        !c.account_no ||
        !c.bank ||
        !c.date ||
        !c.name ||
        !c.number ||
        !c.price ||
        !c.shenase
      )
        return false;
    }

    return true;
  }, [cheques, files]);

  const clearInputs = (): void => {
    setSelectedChequeIdx(0);
    setNrCheques(1);
    setCustomer(null);
    setDesc("");
    setCheques([{ ...INITIAL_CHEQUE }]);
    setFiles([...INITIAL_FILE]);
  };

  const hasUserAccess = useUserStore((s) => s.hasUserAccess);

  const onSubmit = useCallback((): void => {
    if (!hasUserAccess(["ReceiveCheque"]))
      return pushAlert("دسترسی غیر مجاز", "error");

    if (!customer || !desc)
      return pushAlert(
        "لطفا مطمئن شوید که فیلدها به درستی پر شده اند",
        "error"
      );

    if (!validateCheques())
      return pushAlert(
        "لطفا مطمئن شوید که فیلدها به درستی پر شده اند",
        "error"
      );

    FactorApi.recv(customer, desc, cheques, files)
      .then(() => {
        clearInputs();
        pushAlert("عملیات موفقیت آمیز");
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, desc, validateCheques]);

  const onDelete = useCallback(() => {
    if (nrCheques === 1) {
      setCheques([{ ...INITIAL_CHEQUE }]);
      setFiles([...INITIAL_FILE]);
      return;
    }

    setSelectedChequeIdx(0);

    setCheques((prev) => {
      return prev.filter((_, idx) => idx !== selectedChequeIdx);
    });

    setFiles((prev) => {
      return prev.filter((_, idx) => idx !== selectedChequeIdx);
    });

    setNrCheques((prev) => prev - 1);
  }, [selectedChequeIdx, nrCheques]);

  return (
    <div
      style={{
        background: theme.overlay,
        borderRadius: 15,
        minHeight: 200,
        marginTop: "1rem",
        padding: "1rem",
      }}
    >
      <Row
        gap={20}
        style={{ marginBottom: "3rem" }}
        className="finance-input-row"
      >
        <SelectorInput<Customer>
          items={customers}
          uniqueKey={"fullname"}
          selected={customer}
          setSelected={setCustomer}
          stringField={"fullname"}
          renderStrings={["fullname", "phone"]}
          compareFn={(c, v) =>
            c.fullname.toLowerCase().includes(v.toLowerCase()) ||
            c.phone.includes(v)
          }
          label="نام مشتری"
        />
        <TextInput value={desc} setValue={setDesc} label="بابت" />
        <TextInput
          value={totalPrice}
          setValue={setTotalPrice}
          label="کل مبلغ دریافتی (ریال)"
          inputProps={{
            readOnly: true,
            placeholder: "به طور خودکار پر می شود",
          }}
        />
      </Row>
      <Column alignItems={AlignItems.Start} gap={10}>
        <Row style={{ width: "100%" }} justifyContent={JustifyContent.Between}>
          <Heading
            size={20}
            color={theme.textOnOverlay}
            textStyles={{ width: "100%" }}
          >
            مشخصات چک
          </Heading>
          <ChequeCounter
            nrCheques={nrCheques}
            selectedChequeIdx={selectedChequeIdx}
            setSelectedChequeIdx={setSelectedChequeIdx}
          />
        </Row>
        <ChequeForm
          account_no={cheques[selectedChequeIdx].account_no}
          bank={cheques[selectedChequeIdx].bank}
          date={cheques[selectedChequeIdx].date}
          file={files[selectedChequeIdx]}
          name={cheques[selectedChequeIdx].name}
          number={cheques[selectedChequeIdx].number}
          price={cheques[selectedChequeIdx].price}
          shenase={cheques[selectedChequeIdx].shenase}
          onDelete={onDelete}
          onNext={onNext}
          onSubmit={onSubmit}
          setAccountNo={(v) => setChequesValue("account_no", v as string)}
          setBank={(v) => setChequesValue("bank", v as Bank | null)}
          setDate={(v) => setChequesValue("date", v as Timestamp)}
          setName={(v) => setChequesValue("name", v as string)}
          setNumber={(v) => setChequesValue("number", v as string)}
          setPrice={(v) => setChequesValue("price", v as number)}
          setShenase={(v) => setChequesValue("shenase", v as string)}
          setFile={setFilesValue}
          buttons={["delete", "next", "submit"]}
        />
      </Column>
    </div>
  );
};

export default RecieveFactor;
